import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, ExternalLink } from 'lucide-react';

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  learnMoreUrl?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: string;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  learnMoreUrl,
  position = 'top',
  maxWidth = '300px',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), 200);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {isVisible && (
        <div
          className={`absolute ${positionClasses[position]} z-50 pointer-events-auto`}
          role="tooltip"
          aria-live="polite"
        >
          <div
            className="bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg px-4 py-3 shadow-xl border border-gray-700 dark:border-gray-600"
            style={{ maxWidth }}
          >
            <p className="leading-relaxed">{content}</p>
            {learnMoreUrl && (
              <a
                href={learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-blue-300 hover:text-blue-200 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Learn more <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface InfoIconTooltipProps {
  content: string;
  learnMoreUrl?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  iconClassName?: string;
}

export const InfoIconTooltip: React.FC<InfoIconTooltipProps> = ({
  content,
  learnMoreUrl,
  position = 'top',
  iconClassName = '',
}) => {
  return (
    <Tooltip content={content} learnMoreUrl={learnMoreUrl} position={position}>
      <button
        type="button"
        className={`inline-flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full ${iconClassName}`}
        aria-label="More information"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
    </Tooltip>
  );
};
